import React, { useEffect, useState, useCallback, useRef } from 'react';
import { DynamicComponent } from '@/editor/dynamicComponent';
import { useFormContext } from '@/components/form';
import { useStore } from '@tanstack/react-form';

// A simple debounce function
function debounce(func, delay) {
  let timeoutId;
  return function(...args) {
    clearTimeout(timeoutId);
    timeoutId = setTimeout(() => {
      func.apply(this, args);
    }, delay);
  };
}

const RemoteArtifact = React.memo(({ url = "" }) => {
    const [loading, setLoading] = useState(true);
    const [text, setText] = useState('');
    const abortControllerRef = useRef(null);
    
    useEffect(() => {
      // Cancel any existing request
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }

      if (!url) {
        setLoading(false);
        setText('');
        return;
      }

      const fetchText = async () => {
        setLoading(true);
        
        // Create new AbortController for this request
        const abortController = new AbortController();
        abortControllerRef.current = abortController;

        try {
          console.log('Fetching jsx from URL:', url);
          const response = await fetch(url, {
            signal: abortController.signal
          });
          
          if (!response.ok) {
            throw new Error(`Failed to load ${url}`);
          }

          const textContent = await response.text();
          
          // Only update state if request wasn't aborted
          if (!abortController.signal.aborted) {
            setText(textContent);
          }
        } catch (error) {
          // Ignore AbortError - it's expected when we cancel requests
          if (error.name === 'AbortError') {
            //console.log('Fetch aborted for:', url);
            return;
          }
          
          console.error('Error fetching text:', error);
          if (!abortController.signal.aborted) {
            setText('Error fetching text');
          }
        } finally {
          if (!abortController.signal.aborted) {
            //console.log(`Fetch completed ${url}`);
            setLoading(false);
          }
        } 
      };

      fetchText();

      // Cleanup function - abort the request when URL changes or component unmounts
      return () => {
        if (abortControllerRef.current) {
          abortControllerRef.current.abort();
        }
      };
    }, [url]);
    
    //console.log('RemoteArtifact rendered with URL:', url);

    return (
      <>
        {loading ? (
          <p className="text-xs text-muted-foreground mt-2">
            Loading...
          </p>
        ) : (
          <DynamicComponent _jsx={text}  />
        )}
      </>
    );
  },
  (prev, next) => prev.url === next.url
);

export { RemoteArtifact };